import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { try_for_free } from "../../url";
import { useCurrency } from "../../currency/CurrencyContext";

const PRICING_TEASER_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rpt-ink: #163A5A; --rpt-ink2: #3D6A8C; --rpt-ink3: #5E87A3;
    --rpt-sky: #1A63A5; --rpt-mint: #2AB6E8;
    --rpt-secondary: #2AB6E8;
    --rpt-secondary-deep: #1AA3D4;
    --rpt-secondary-soft: #D9F4FC;
    --rpt-sky-light: #E8F4FC;
    --rpt-sky-hover: #155a94;
    --rpt-white: #FFFFFF;
    --rpt-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; 
    --rpt-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }

  .rpt-price-band { background: linear-gradient(135deg, #E8F4FC 0%, var(--rpt-secondary-soft) 45%, #E8F8FD 100%); padding: 80px 40px; font-family: var(--rpt-ff-body); color: var(--rpt-ink); }
  .rpt-price-inner { max-width: 1240px; margin: 0 auto; display: grid; grid-template-columns: 1fr 400px; gap: 60px; align-items: center; }
  
  .rpt-section-label { display: inline-block; font-size: 11px; font-weight: 700; color: var(--rpt-sky); background: var(--rpt-sky-light); padding: 4px 10px; border-radius: 6px; letter-spacing: .08em; margin-bottom: 16px; text-transform: uppercase; }
  .rpt-h2 { font-family: var(--rpt-ff-head); font-size: clamp(32px, 4vw, 44px); font-weight: 800; line-height: 1.1; letter-spacing: -.02em; color: var(--rpt-ink); margin-bottom: 16px; }
  
  .rpt-price-num { font-family: var(--rpt-ff-head); font-size: clamp(56px, 7vw, 84px); font-weight: 800; line-height: 1; letter-spacing: -.03em; color: var(--rpt-sky); }
  .rpt-price-unit { font-size: 16px; color: var(--rpt-ink2); font-weight: 400; }
  .rpt-price-note { font-size: 13px; color: var(--rpt-ink3); margin-top: 6px; }
  
  .rpt-price-perks { list-style: none; margin: 28px 0; padding: 0; }
  .rpt-price-perks li {
    font-size: 14px; color: var(--rpt-ink2); padding: 8px 0;
    display: flex; align-items: center; gap: 10px; border-bottom: 1px solid rgba(26,99,165,.12);
  }
  .rpt-price-perks li::before { content: '✓'; color: var(--rpt-sky); font-weight: 600; font-size: 13px; flex-shrink: 0; }
  
  .rpt-trial-card {
    background: var(--rpt-white); border-radius: 20px; padding: 32px;
    border: 1px solid rgba(26,99,165,.18); box-shadow: 0 16px 48px rgba(26,99,165,.1);
    text-align: left;
  }
  .rpt-trial-tag { font-size: 11px; font-weight: 700; color: var(--rpt-sky); letter-spacing: .08em; margin-bottom: 8px; text-transform: uppercase; }
  .rpt-trial-title { font-family: var(--rpt-ff-head); font-size: 24px; font-weight: 800; color: var(--rpt-ink); margin-bottom: 6px; }
  .rpt-trial-sub { font-size: 13px; color: var(--rpt-ink3); margin-bottom: 24px; line-height: 1.5; }
  
  .rpt-email-row { display: flex; gap: 8px; margin-bottom: 12px; }
  .rpt-email-input {
    flex: 1; border: 1px solid #D0D4E8; border-radius: 8px; padding: 10px 14px;
    font-size: 14px; font-family: var(--rpt-ff-body); color: var(--rpt-ink); outline: none;
    transition: border-color .2s; min-width: 0;
  }
  .rpt-email-input:focus { border-color: var(--rpt-sky); }
  .rpt-email-btn {
    background: var(--rpt-sky); color: #fff; border: none; border-radius: 8px;
    padding: 10px 18px; font-size: 13.5px; font-weight: 600; font-family: var(--rpt-ff-body); cursor: pointer;
    transition: background .2s; white-space: nowrap; flex-shrink: 0;
  }
  .rpt-email-btn:hover { background: var(--rpt-sky-hover); }
  
  .rpt-terms { font-size: 11px; color: var(--rpt-ink3); line-height: 1.5; }
  .rpt-terms a { color: var(--rpt-sky); text-decoration: none; font-weight: 500; }

  @media (max-width: 1024px) {
    .rpt-price-inner { grid-template-columns: 1fr; gap: 40px; }
    .rpt-h2 { font-size: 34px; }
    .rpt-price-num { font-size: 68px; }
  }
  @media (max-width: 600px) {
    .rpt-price-band { padding: 48px 20px; }
    .rpt-h2 { font-size: 28px; }
    .rpt-price-num { font-size: 52px; }
    .rpt-email-row { flex-direction: column; }
    .rpt-email-btn { width: 100%; justify-content: center; }
  }
`;

function RefPricingTeaser() {
  const { t } = useTranslation('home');
  const { format } = useCurrency();
  const [email, setEmail] = useState("");

  const handleGetStarted = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      window.open(try_for_free, "_blank");
      return;
    }

    const registerUrl = new URL(try_for_free, window.location.origin);
    registerUrl.searchParams.set("prefilled_email", trimmedEmail);
    window.open(registerUrl.toString(), "_blank");
  };

  const perks = t('pricingTeaser.perks', { returnObjects: true }) || [];

  return (
    <>
      <style>{PRICING_TEASER_STYLES}</style>
      <div className="rpt-price-band">
        <div className="rpt-price-inner">
          <div>
            <div className="rpt-section-label">{t('pricingTeaser.label')}</div>
            <h2 className="rpt-h2">
              {t('pricingTeaser.titleLine1')}<br />
              {t('pricingTeaser.titleLine2')}
            </h2>
            <div>
              <span className="rpt-price-num">{format(7.42)}</span>
              <span className="rpt-price-unit">{t('pricingTeaser.unit')}</span>
            </div>
            <div className="rpt-price-note">{t('pricingTeaser.note', { price: format(89) })}</div>
            <ul className="rpt-price-perks">
              {Array.isArray(perks) && perks.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
          <div className="rpt-trial-card">
            <div className="rpt-trial-tag">{t('pricingTeaser.trialTag')}</div>
            <div className="rpt-trial-title">{t('pricingTeaser.trialTitle')}</div>
            <div className="rpt-trial-sub">{t('pricingTeaser.trialSub')}</div>
            <div className="rpt-email-row">
              <input
                className="rpt-email-input"
                type="email"
                placeholder={t('pricingTeaser.emailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleGetStarted();
                  }
                }}
              />
              <button className="rpt-email-btn" onClick={handleGetStarted}>
                {t('pricingTeaser.getStarted')}
              </button>
            </div>
            <div className="rpt-terms">
              {t('pricingTeaser.termsNotice')}{" "}
              <a href="/terms">{t('pricingTeaser.terms')}</a> {t('pricingTeaser.and')}{" "}
              <a href="/privacy">{t('pricingTeaser.privacy')}</a>.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RefPricingTeaser;
